import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from '../utils/axios';
import { HOST_API } from '../config';
import useAuth from './useAuth';

// One socket, two tables. The consumer decides which feed a connection reads
// from the user's role, and so does this hook: operator staff read
// AdminNotification under admin/, members read their own rows under user/.
// The two serialise to the same field names on purpose, so everything below
// this line is shared.
const useNotifications = () => {
  const { user } = useAuth();
  // Null until the role is known. Asking before then would send half the
  // members to the operator feed on a cold load and get a 403 for it.
  const basePath = useMemo(() => {
    if (!user?.role) return null;
    return user.role === 'org' ? '/api/v1/notification/admin' : '/api/v1/notification/user';
  }, [user?.role]);
  const basePathRef = useRef(basePath);
  basePathRef.current = basePath;
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 10;
  const reconnectDelay = 3000;

  // Fetch initial notifications from REST API
  const fetchNotifications = useCallback(async () => {
    if (!basePath) return;

    try {
      setIsLoading(true);
      
      // Fetch notifications list and unread count
      const [notifResponse, countResponse] = await Promise.all([
        axios.get(`${basePath}/`),
        axios.get(`${basePath}/unread_count/`)
      ]);
      
      // Handle both paginated and non-paginated responses
      const notificationData = notifResponse.data.results || notifResponse.data;
      
      setNotifications(notificationData);
      setUnreadCount(countResponse.data.count);
      setError(null);
      
      console.log('✅ Fetched notifications:', notificationData.length);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
      setError('Failed to load notifications');
    } finally {
      setIsLoading(false);
    }
  }, [basePath]);

  const connect = useCallback(() => {
    // Get access token (matches your axios pattern)
    const accessToken = localStorage.getItem('accessToken');

    if (!basePath) {
      return;
    }

    if (!accessToken) {
      console.log('No access token, skipping WebSocket connection');
      return;
    }

    // Convert HTTP/HTTPS to WS/WSS
    const wsProtocol = HOST_API.startsWith('https') ? 'wss' : 'ws';
    const wsHost = HOST_API.replace(/^https?:\/\//, '');
    const wsUrl = `${wsProtocol}://${wsHost}/ws/notifications/?token=${accessToken}`;

    try {
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log('✅ WebSocket Connected');
        setIsConnected(true);
        setError(null);
        reconnectAttemptsRef.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'notification') {
            // Add new notification to the list
            setNotifications(prev => [data.notification, ...prev]);
            setUnreadCount(prev => prev + 1);
            
            console.log('📨 New notification received:', data.notification.title);
            
            // Optional: Show browser notification
            if ('Notification' in window && Notification.permission === 'granted') {
              new Notification(data.notification.title, {
                body: data.notification.message,
                icon: '/logo192.png',
                badge: '/logo192.png',
              });
            }
          } else if (data.type === 'unread_count') {
            setUnreadCount(data.count);
            console.log('📊 Unread count updated:', data.count);
          } else if (data.type === 'connection_established') {
            console.log('📡 WebSocket connection established:', data.message);
          } else if (data.type === 'mark_read_success') {
            console.log('✓ Notification marked as read:', data.notification_id);
          } else if (data.type === 'mark_all_read_success') {
            console.log('✓ All notifications marked as read:', data.count);
          }
        } catch (err) {
          console.error('Error parsing WebSocket message:', err);
        }
      };

      ws.onerror = (error) => {
        console.error('❌ WebSocket Error:', error);
        setError('WebSocket connection error');
      };

      ws.onclose = (event) => {
        console.log('🔌 WebSocket Disconnected:', event.code, event.reason);
        setIsConnected(false);
        wsRef.current = null;

        // Attempt to reconnect if not a normal closure
        if (event.code !== 1000 && reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectAttemptsRef.current += 1;
          console.log(`🔄 Reconnecting... Attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts}`);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, reconnectDelay);
        } else if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
          setError('Please retry');
        }
      };

      wsRef.current = ws;
    } catch (err) {
      console.error('Failed to create WebSocket:', err);
      setError('Failed to create WebSocket connection');
    }
  }, [basePath]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'User initiated disconnect');
      wsRef.current = null;
    }
  }, []);

  const markAsRead = useCallback(async (notificationId) => {
    // Optimistically update UI immediately
    setNotifications(prev =>
      prev.map(notif =>
        notif.id === notificationId ? { ...notif, is_read: true } : notif
      )
    );
    setUnreadCount(prev => Math.max(0, prev - 1));

    // Try WebSocket first
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        action: 'mark_read',
        notification_id: notificationId
      }));
    } else {
      // Fallback to REST API
      try {
        await axios.post(`${basePathRef.current}/${notificationId}/mark_read/`);
        console.log('✓ Notification marked as read via REST API');
      } catch (error) {
        console.error('Failed to mark notification as read:', error);
        // Revert optimistic update
        setNotifications(prev =>
          prev.map(notif =>
            notif.id === notificationId ? { ...notif, is_read: false } : notif
          )
        );
        setUnreadCount(prev => prev + 1);
      }
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    // Optimistically update UI
    const previousNotifications = notifications;
    const previousCount = unreadCount;
    
    setNotifications(prev =>
      prev.map(notif => ({ ...notif, is_read: true }))
    );
    setUnreadCount(0);

    // Try WebSocket first
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        action: 'mark_all_read'
      }));
    } else {
      // Fallback to REST API
      try {
        await axios.post(`${basePathRef.current}/mark_all_read/`);
        console.log('✓ All notifications marked as read via REST API');
      } catch (error) {
        console.error('Failed to mark all as read:', error);
        // Revert optimistic update
        setNotifications(previousNotifications);
        setUnreadCount(previousCount);
      }
    }
  }, [notifications, unreadCount]);

  // Initialize - fetch data and connect WebSocket
  useEffect(() => {
    fetchNotifications();
    connect();
    
    // Request browser notification permission
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }

    return () => {
      disconnect();
    };
  }, [connect, disconnect, fetchNotifications]);

  // Reconnect on token change
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'accessToken') {
        disconnect();
        setTimeout(() => {
          fetchNotifications();
          connect();
        }, 1000);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [connect, disconnect, fetchNotifications]);

  return {
    notifications,
    unreadCount,
    isConnected,
    isLoading,
    error,
    markAsRead,
    markAllAsRead,
    refreshNotifications: fetchNotifications,
    reconnect: connect,
  };
};

export default useNotifications;